import type { PolicyDraft, PolicyOrigin, PolicySettings, PolicyTarget, ResolvedDraft, SettingKey } from './contracts';
import { settingKeys } from './contracts';

// Proposed starting settings, never evidence of applied protection or approved patching.
export const defaultSettings: PolicySettings = {
  patchApproval: 'manual', maintenanceWindow: { timeZone: 'UTC', days: [0], startMinute: 120, durationMinutes: 180 },
  reboot: 'never', excluded: false,
  defenderRealTime: true, defenderCloud: true, defenderPua: true, smartScreen: true,
  networkProtection: true, edgeProtection: true, chromeProtection: true, preventBrowserBypass: true,
  updateRing: 'everyone', qualityDeferralDays: 7, targetVersion: '25H2', activeHours: { startHour: 5, endHour: 23 }, installHour: 3,
};
/** What each Windows Update ring sets for a setting no layer sets (decided table, WRMM-328). Pilot may move to 26H2 later. */
export const ringDefaults = {
  pilot: { qualityDeferralDays: 0, targetVersion: '25H2' },
  everyone: { qualityDeferralDays: 7, targetVersion: '25H2' },
} as const satisfies Record<PolicySettings['updateRing'], Pick<PolicySettings, 'qualityDeferralDays' | 'targetVersion'>>;
export const ringFollowing = ['qualityDeferralDays', 'targetVersion'] as const;
const followsRing = (key: SettingKey): key is typeof ringFollowing[number] => (ringFollowing as readonly string[]).includes(key);
export function targetKey(t: PolicyTarget): string {
  return t.kind === 'customer' ? `customer:${t.organizationId}` : t.kind === 'site' ? `site:${t.siteId}` : `device:${t.deviceId}`;
}
const origin = (d: PolicyDraft): PolicyOrigin => ({ kind: d.target.kind,
  id: d.target.kind === 'customer' ? d.target.organizationId : d.target.kind === 'site' ? d.target.siteId : d.target.deviceId, revision: d.revision });
export const sameValue = (a: unknown, b: unknown): boolean => {
  // Window days are a set. Compare values rather than JSON object insertion order.
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && [...a].sort().every((v, i) => sameValue(v, [...b].sort()[i]));
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const aa = a as Record<string, unknown>, bb = b as Record<string, unknown>;
    return Object.keys(aa).length === Object.keys(bb).length && Object.keys(aa).every(k => sameValue(aa[k], bb[k]));
  }
  return a === b;
};

/** Ordered customer, site, device layers, already scoped by the caller. Expiry removes both values and locks. */
export function resolveDraft(layers: PolicyDraft[], now: number): ResolvedDraft {
  const base: PolicyOrigin = { kind: 'default', id: null, revision: 1 };
  const out: ResolvedDraft = { settings: structuredClone(defaultSettings),
    origins: Object.fromEntries(settingKeys.map(k => [k, base])) as Record<SettingKey, PolicyOrigin>, lockedBy: {}, conflicts: [], expired: [] };
  for (const layer of layers) {
    const source = origin(layer);
    if (layer.expiresAt && Date.parse(layer.expiresAt) <= now) { out.expired.push(source); continue; }
    for (const key of settingKeys) {
      const value = layer.settings[key];
      if (value === undefined) continue;
      const locked = out.lockedBy[key];
      if (locked) {
        if (!sameValue(value, out.settings[key])) out.conflicts.push({ field: key, attempted: source, kept: locked });
        continue;
      }
      // Settings are validated before storage; assign a single key without widening to arbitrary properties.
      Object.assign(out.settings, { [key]: structuredClone(value) });
      out.origins[key] = source;
    }
    for (const key of layer.locks) {
      if (out.lockedBy[key]) continue;
      // Locking a value that still follows the ring pins the ring's value at this layer (keeping its origin, as any locked
      // inherited value does), so a child's ring can't change it.
      if (followsRing(key) && out.origins[key].kind === 'default') Object.assign(out.settings, { [key]: ringDefaults[out.settings.updateRing][key] });
      out.lockedBy[key] = source;
    }
    // An exclusion is a safety boundary: a narrower draft cannot silently include its children again.
    if (out.settings.excluded) out.lockedBy.excluded ??= out.origins.excluded;
  }
  // A ring-following setting no layer set or locked takes the resolved ring's value.
  for (const key of ringFollowing) if (out.origins[key].kind === 'default' && !out.lockedBy[key]) Object.assign(out.settings, { [key]: ringDefaults[out.settings.updateRing][key] });
  return out;
}
